import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckSyncData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import styles from './PMSandBE.module.scss'
import SyncRow from './Row'
import { ReactComponent as BugIcon } from './Row/bug.svg'
import PMSBEHealthTitleRow from './TitleRow'

const allowedCsmIdsForFiltering = [
  '1840012',
  '1839965',
  '1840697',
  '1840729',
  '1840528',
  '1839964'
]

const HealthCheckPMS = () => {
  const dispatch = useDispatch()

  const [debugData, setDebugData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('pms')

  useEffect(() => {
    dispatch(getHealthCheckSyncData())
  }, [dispatch])

  const isLoading = useSelector(s => s.healthCheck.sync.isLoading)
  const hotelData = useSelector(s => s.healthCheck.sync.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.sync.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.sync.hotelChains)
  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(false)

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function getFilteredResults(hotelData) {
    const { searchString, chains, brands, hotelGroups, subsidiaryStatus, pms, csmIds } =
      filterState

    return hotelData
      .filter(h => allowedCsmIdsForFiltering.includes(String(h.csmUserId)))
      .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
      .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
      .filter(h => (chains === 'all' ? true : h.chainCode === chains))
      .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
      .filter(h => {
        if (pms.length === 0) {
          return true
        } else {
          return pms.includes(h.pms)
        }
      })
      .filter(h => {
        if (subsidiaryStatus.length === 0) {
          return true
        } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
          return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
        } else {
          return subsidiaryStatus.includes(h.subsidiaryStatus)
        }
      })
      .filter(h => {
        if (csmIds.length === 0) {
          return true
        } else {
          return csmIds.includes(`${h.csmUserId}`)
        }
      })
  }

  function getSortedResults(hotelData, sortBy, sortDecreasing) {
    const titleRowNames = [
      'displayId',
      'shortName',
      'pmsSyncIsConnected',
      'nextStartDate',
      'pmsSyncTotalReportsReceivedInLast48Hours',
      '',
      'nextRunDateTime',
      'lastResult',
      'csmIds'
    ]

    const sortByProp = titleRowNames[sortBy]

    const sortedData = hotelData.sort((a, b) => {
      if (
        sortByProp === 'bookingEngineLastInfoReceivedOn' ||
        sortByProp === 'pmsSyncLastInfoReceivedOn' ||
        sortByProp === 'nextStartDate' ||
        sortByProp === 'nextRunDateTime'
      ) {
        return (
          (b[sortByProp] ? Date.parse(b[sortByProp]) : 0) -
          (a[sortByProp] ? Date.parse(a[sortByProp]) : 0)
        )
      }

      if (typeof a[sortByProp] === 'string') {
        if (a[sortByProp] === b[sortByProp]) {
          return 0
        }
        return a?.[sortByProp]?.toUpperCase() > b?.[sortByProp]?.toUpperCase()
      } else if (typeof a[sortByProp] === 'boolean') {
        return Number(b[sortByProp]) - Number(a[sortByProp])
      } else {
        return b[sortByProp] - a[sortByProp]
      }
    })

    return sortDecreasing ? sortedData : sortedData.reverse()
  }

  return (
    <div className={styles.wrapper}>
      {debugData && (
        <ConfirmationDialog title="Problem details" onClose={() => setDebugData(null)}>
          <pre>
            {JSON.stringify(JSON.parse(debugData.replace(/(\r\n|\n|\r)/gm, '')), null, 4)}
          </pre>
        </ConfirmationDialog>
      )}
      <CustomHeader title="Hotel health check" width={1180} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <button
          onClick={() => dispatch(getHealthCheckSyncData())}
          className={styles.reloadButton}
        >
          <RefreshIcon /> Reload results
        </button>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus
        showPMSFilter
        showCsmFilter
      />

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={100} />
        ) : (
          <>
            <div className={styles.legends}>
              <div className={styles.legend}>
                <CheckIcon width={16} height={16} /> PMS is connected
              </div>
              <div className={styles.legend}>
                <CrossIcon width={16} height={16} /> PMS is not connected
              </div>
              <div className={styles.legend}>
                <CrossIcon width={16} height={16} fill="red" />
                Not synced for 14 days and turned off{' '}
              </div>
              <div className={styles.legend}>
                <BugIcon width={22} fill="red" /> PMS sync problems
              </div>
            </div>
            <Table
              widths={[40, 240, 10, 50, 100, 110, 90, 140, 240]}
              handleTitleCellClick={handleTitleCellClick}
              sortIndex={sortByIndex}
              sortDirection={sortDecreasing ? 'down' : 'up'}
            >
              <PMSBEHealthTitleRow />
              {getSortedResults(
                getFilteredResults(hotelData),
                sortByIndex,
                sortDecreasing
              ).map((h, i) => (
                <SyncRow {...h} index={i + 1} handleSetDebugData={setDebugData} />
              ))}
            </Table>
          </>
        )}
      </div>
    </div>
  )
}

export default HealthCheckPMS
